import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  ProjectsResponseSchema,
  PullRequestProjectSchema,
  type ProjectsResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'

import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

export type MutationData = {
  addProjects: string[]
  removeItems: string[]
  nextProjects: ProjectPickerBaseProject[]
}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useModifyProjects({onError}: Callbacks = {}) {
  const projectsQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'projects')
  const addProjectUrl = usePageDataUrl(PageData.addProject)
  const removeProjectUrl = usePageDataUrl(PageData.removeProject)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const results = await Promise.allSettled([
        ...data.addProjects.map(async projectId => {
          const response = await fetchWithErrorHandling(addProjectUrl, {
            method: 'POST',
            headers: {Accept: 'application/json'},
            body: {projectId},
          })
          const json = await parseJSONWithBetterErrors(response)
          throwErrorsIfBadResponse(response, json)
        }),
        ...data.removeItems.map(async itemId => {
          const response = await fetchWithErrorHandling(removeProjectUrl, {
            method: 'DELETE',
            headers: {Accept: 'application/json'},
            body: {itemId},
          })
          const json = await parseJSONWithBetterErrors(response)
          throwErrorsIfBadResponse(response, json)
        }),
      ])
      for (const result of results) {
        if (result.status === 'rejected') throw result.reason
      }
    },
    onMutate: async ({nextProjects}) => {
      if (!projectsQueryKey) return

      await queryClient.cancelQueries({queryKey: projectsQueryKey})
      const previous = queryClient.getQueryData<ProjectsResponse>(projectsQueryKey)

      queryClient.setQueryData<ProjectsResponse>(projectsQueryKey, () =>
        create(ProjectsResponseSchema, {
          projects: nextProjects.map(project =>
            create(PullRequestProjectSchema, {
              id: project.id,
              title: project.title,
              number: project.number,
              url: project.url,
              closed: project.closed,
              // The real item id is only known once the server creates it; refetching on
              // settle (below) replaces this placeholder with the authoritative value.
              itemId: '',
            }),
          ),
        }),
      )

      return {previous}
    },
    onError: (e: Error, _vars, onMutateResult) => {
      if (projectsQueryKey && onMutateResult?.previous) {
        queryClient.setQueryData(projectsQueryKey, onMutateResult.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!projectsQueryKey) return
      return queryClient.invalidateQueries({queryKey: projectsQueryKey})
    },
  })
}
